import React, { useState } from "react";
import {
  Box,
  Card,
  CardActionArea,
  Chip,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

/**
 * Example Boards — finished science-fair boards from Ardent students.
 *
 * Step 8 asks a student to present their findings, and until now the only
 * description of what "presented" looks like was words. These are the real
 * thing: what a board contains, how much of it is data rather than prose, and
 * how the sections a student has been filling in since Step 1 end up laid out
 * on three panels.
 *
 * Photographs live on the CDN, never in the repo (scripts/push-asset.sh in
 * ardent-research; immutable keys, so a new picture is a new name). The grid
 * loads thumbnails — the full-size board is fetched only when one is opened.
 *
 * These carry the students' own names and schools, as they did on the fair
 * floor. That is why this page sits behind the student login and not on the
 * public site.
 */

const CDN = "https://assets.ardentacademy.com/research/boards";

interface Board {
  slug: string;
  title: string;
  award: string | null;
}

/* Awards as James recorded them. A board with no award recorded shows none —
   it is here because it is a good board, not because it placed. */
const BOARDS: Board[] = [
  /* 2nd Place at STS leads: it is the highest award any of this work has
     earned, and it is what a student asking "how good does this get?" wants
     to see. The ISEF chips that follow say ISEF rather than a placing —
     none was recorded. */
  {
    slug: "heart-phase-field",
    title:
      "Electromechanical Modeling of the Heart in Moving Domains Using the Phase-Field Method",
    award: "2nd Place, STS",
  },
  {
    slug: "collatz-conjecture",
    title:
      "A Novel Approach to Collatz Conjecture Proof: Effect of Addition on Prime Factorization and Unique Numeric Potential Concept",
    award: "ISEF",
  },
  {
    slug: "autonomous-convoy",
    title: "Autonomous Vehicle for Convoys: Evaluation by Hausdorff Distance",
    award: "ISEF",
  },
  {
    slug: "violin-electrification",
    title:
      "Automatically Reproducing Live Performances of Violins via Novel Sound-Post Controlled Electrification",
    award: "ISEF",
  },
  {
    slug: "recruitment-detection",
    title:
      "Automating Identification of Terrorist Recruitment on Social Media: A Deep Learning Approach",
    award: "ISEF",
  },
  {
    slug: "graphite-conductivity",
    title: "Graphite / Conductor: What factors affect the conductivity of graphite lines?",
    award: "Broadcom Master Finalist",
  },
  {
    slug: "jellyfish-insulin",
    title:
      "Bioinformatic Analysis of the Insulin Signaling Pathway in the Moon Jellyfish Aurelia",
    award: "STS Finalist",
  },
  {
    slug: "relativistic-ray-tracing",
    title:
      "Relativistic Ray Tracing: the appearance of objects in relativistic situations",
    award: "STS Finalist",
  },
  {
    slug: "wordrank-search",
    title:
      "Beating Google: Development of the Novel Web Page Ranking Algorithm WordRank in a Search Engine",
    award: "OCSEF Winner",
  },
  {
    slug: "zipfs-law-music",
    title: "Zipf's Law and the Computational Identification of Music",
    award: "OCSEF Winner",
  },
  {
    slug: "video-games-doodling",
    title: "Impact of Playing Video Games vs. Doodling on Tests",
    award: "CSEF Winner",
  },
];

export default function ExampleBoardsPage() {
  const [open, setOpen] = useState<Board | null>(null);

  return (
    <Box sx={{ py: 2, maxWidth: 1100 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
        Example Boards
      </Typography>

      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
          },
        }}
      >
        {BOARDS.map((b) => (
          <Card key={b.slug} variant="outlined" sx={{ borderRadius: 2 }}>
            <CardActionArea onClick={() => setOpen(b)}>
              <Box
                component="img"
                src={`${CDN}/${b.slug}-thumb.jpg`}
                alt={b.title}
                loading="lazy"
                /* contain, not cover: a cropped board loses its title and its
                   outer panels, which are the two things a student is here to
                   look at. Letterboxing is the cheaper price. */
                sx={{
                  width: "100%",
                  aspectRatio: "4 / 3",
                  objectFit: "contain",
                  display: "block",
                  bgcolor: "grey.100",
                }}
              />
              <Box sx={{ p: 1.5 }}>
                {b.award && (
                  <Chip
                    size="small"
                    color="warning"
                    label={b.award}
                    sx={{ mb: 0.75 }}
                  />
                )}
                <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.45 }}>
                  {b.title}
                </Typography>
              </Box>
            </CardActionArea>
          </Card>
        ))}
      </Box>

      {/* Full size — fetched only now, which is why the grid stays light. */}
      <Dialog
        open={!!open}
        onClose={() => setOpen(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogContent sx={{ p: 0, position: "relative" }}>
          <IconButton
            onClick={() => setOpen(null)}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              bgcolor: "rgba(255,255,255,0.9)",
              "&:hover": { bgcolor: "rgba(255,255,255,1)" },
            }}
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
          {open && (
            <>
              <Box
                component="img"
                src={`${CDN}/${open.slug}.jpg`}
                alt={open.title}
                sx={{ width: "100%", display: "block" }}
              />
              <Box sx={{ p: 2 }}>
                {open.award && (
                  <Chip
                    size="small"
                    color="warning"
                    label={open.award}
                    sx={{ mb: 1 }}
                  />
                )}
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {open.title}
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
